import { useMutation, useQuery } from '@apollo/client';
import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { HookFormSelectionListSingle } from 'lib-shared-hook-form';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import {
  CreateOAuthProviderArg,
  createOAuthProviderMutation,
  CreateOAuthProviderResponse,
  OAuthProvider,
  OAuthSetting,
  OAuthStrategiesResponse,
  OAuthStrategy,
  readOAuthProviderStrategiesQuery,
} from 'apis/partnerIntegrations';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { OAuthProviderSettingsConfiguration } from '../../components/settingsConfiguration/oauthProviderSettingsConfiguration';
import { useOAuthProviderConfigurationForm } from '../../hooks/useOAuthProviderConfigurationForm';
import styles from '../../oauthProviderConfigurationModal.module.scss';
import type {
  OAuthProviderConfigurationFormValues,
  OAuthProviderConfigurationModalResult,
} from '../../oauthProviderConfigurationModal.types';
import { OAuthProviderConfigurationModalLoading } from '../components/oauthProviderConfigurationModalLoading/oauthProviderConfigurationModalLoading';

type OAuthProviderCreateModalProps = {
  isOpen: boolean;
  meta?: {
    resolve: (result: OAuthProviderConfigurationModalResult) => void;
  };
  hide: VoidFunction;
};

type StrategyFormValues = {
  strategy: OAuthStrategy;
};

/** A modal for creating OAuth providers */
export const OAuthProviderModalCreate = ({
  isOpen,
  meta,
  hide,
}: OAuthProviderCreateModalProps): JSX.Element => {
  const dispatch = useAppDispatch();

  // State for strategies
  const [strategies, setStrategies] = useState<OAuthStrategy[]>([]);

  const getStepTitle = () => {
    return 'Create OAuth provider';
  };

  const formId = uuid();
  const notify = useNotifications();

  // Form for strategy selection
  const strategyForm = useForm<StrategyFormValues>({
    defaultValues: { strategy: undefined },
  });

  const selectedStrategy = strategyForm.watch('strategy');

  const { schema, values, isSchemaLoading, schemaError } =
    useOAuthProviderConfigurationForm({
      mode: 'create',
      strategyId: selectedStrategy?.name,
    });

  const form = useForm<OAuthProviderConfigurationFormValues>({
    defaultValues: {},
    values,
  });

  // Query to get available strategies
  const { loading: isStrategiesLoading } = useQuery<OAuthStrategiesResponse>(
    readOAuthProviderStrategiesQuery,
    {
      onCompleted(data) {
        const strategiesData = data.Management.oAuthProviderStrategies;
        setStrategies([...strategiesData]);

        // If no strategy selected yet, select the first one
        if (!selectedStrategy && strategiesData.length > 0) {
          const firstStrategy = strategiesData[0];
          strategyForm.setValue('strategy', firstStrategy);
        }
      },
    },
  );

  const [createProvider, { loading: isCreatingProvider }] = useMutation<
    CreateOAuthProviderResponse,
    CreateOAuthProviderArg
  >(createOAuthProviderMutation, {
    onError: (err) => {
      notify(
        {
          title: 'Error while creating the OAuth provider.',
          text: err.cause?.toString() ?? err?.toString(),
        },
        NotificationType.Error,
      );
    },
    onCompleted: (result) => {
      const provider = result.Management.createOAuthProvider;

      notify(
        {
          title: 'Success',
          text: `"${provider.name}" OAuth provider has been created.`,
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      // Close current modal and open update modal for authorization
      hide();

      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:oauthProviderConfiguration',
            arg: {
              mode: 'update',
              id: provider.id,
            },
          },
        }),
      );

      resolveModalResponse(provider);
    },
  });

  const isLoading = isCreatingProvider || isStrategiesLoading;

  const onSubmit = (formData: OAuthProviderConfigurationFormValues) => {
    const settings: OAuthSetting[] = Object.entries(formData)
      .filter(([key, _]) => key !== 'name')
      .map(([key, setting]) => ({
        key,
        value: setting.value,
        secret: setting.secret,
      }));

    createProvider({
      variables: {
        name: formData.name.value as string,
        strategy: selectedStrategy.name,
        settings,
      },
    });
  };

  const resolveModalResponse = useCallback(
    (provider: OAuthProvider) => {
      meta?.resolve({
        isSuccessful: true,
        provider,
      });
    },
    [meta],
  );

  const renderSettings = () => {
    if (!selectedStrategy) {
      return null;
    }

    if (isSchemaLoading) {
      return <OAuthProviderConfigurationModalLoading />;
    }

    if (schemaError) {
      return (
        <FormField>
          <Text.Paragraph>
            Failed to load OAuth provider configuration schema. Please try again
            later or contact support.
          </Text.Paragraph>
        </FormField>
      );
    }

    return <OAuthProviderSettingsConfiguration mode="create" schema={schema} />;
  };

  // Determine what content to show based on the current step
  const renderStepContent = () => {
    if (isLoading) {
      return <OAuthProviderConfigurationModalLoading />;
    }

    return (
      <>
        <FormField>
          <HookFormSelectionListSingle
            name="strategy"
            control={strategyForm.control}
            label="OAuth strategies"
            rules={{ required: 'Please select an OAuth provider type' }}
            componentProps={{
              equalityFn: (a, b) => a === b,
              getItemTitleByValue: (value) => value?.label ?? '',
            }}
          >
            {strategies.map((strategy) => (
              <SelectionList.Item key={strategy.name} value={strategy}>
                {strategy.label}
              </SelectionList.Item>
            ))}
          </HookFormSelectionListSingle>
        </FormField>

        {renderSettings()}
      </>
    );
  };

  // Determine the footer buttons based on the current step
  const renderModalFooter = () => {
    return (
      <ModalFoot
        confirmName="Save"
        cancelName="Cancel"
        cancelOnClick={hide}
        confirmOnClick={form.handleSubmit(onSubmit)}
        confirmProps={{
          type: 'submit',
          form: formId,
          isLoading,
          disabled: isLoading || !selectedStrategy,
        }}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={hide}
      className={styles.oauthProviderConfigurationModal}
    >
      <ModalHead
        title={<Text.Heading>{getStepTitle()}</Text.Heading>}
        onClose={hide}
      />

      <ModalBody>
        <FormProvider {...form}>
          <form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
            <FormGrid>{renderStepContent()}</FormGrid>
          </form>
        </FormProvider>
      </ModalBody>

      {renderModalFooter()}
    </Modal>
  );
};
